import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, Renderer2} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationContextService, IAddressModel, IAddressSearchModel, ICountryDtoModel, LocationService, RolePermissionService } from 'lib-core-shared';
import {Observable} from 'rxjs';
import {debounceTime, filter, map, mergeMap, tap} from 'rxjs/operators';
import { ComponentBaseDirective } from '../directives/component.directive';

@Component({
  selector: 'exui-search-location',
  templateUrl: './search-location.component.html',
  styleUrls: ['./search-location.component.scss']
})
export class SearchLocationComponent extends ComponentBaseDirective implements OnInit {
  @Input() public control: AbstractControl | undefined;
  @Input()  public disabled: boolean | undefined = undefined;
  @Input() public singleMode: boolean = false;
  @Input() public delay?: number = 500;
  @Input() public form: FormGroup;
  @Input() public showAutocomplete: boolean = false;
  @Input() public locations: IAddressSearchModel[] = [];
  @Output() public formEmitter = new EventEmitter<FormGroup>();
  @Output() public locationSelected = new EventEmitter<IAddressModel>();
  @Output() public locationInputChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() public searchLocationChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public countryChanged = new EventEmitter<ICountryDtoModel>();
  public searchLocationErrorLoading: string = 'Error with address lookup service, try entering manually instead';
  public getAddressResultDetailsErrorLoading: string = 'Error retrieving full details of chosen address';
  public readonly minSearchCharacters = 3;
  public term: string = '';
  private pReset: boolean = true;
  public showPicker: boolean = false;
  private justSelected: boolean = false;
  public isBusy: boolean = false;
  @Input() public countries: ICountryDtoModel[] = [];
  public searchJustCompleted: boolean = false;
  @Output() public searchResultEmitter = new EventEmitter<IAddressSearchModel[]>();
  @Output() public errorEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private readonly locationService: LocationService, 
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public roleService: RolePermissionService,
    private readonly formBuilder: FormBuilder, 
    private readonly cd: ChangeDetectorRef,
    private renderer: Renderer2,
    public applicationContext: ApplicationContextService) {
    super(activatedRoute, router, roleService, applicationContext);
    this.form = this.formBuilder.group({
        searchTerm: [''],
        country: [null, Validators.required]
      });
  }

  public get searchControl(): FormControl {
    return this.form.controls.searchTerm as FormControl
  }

  public get reset(): boolean {
    return this.pReset;
  }

  @Input()
  public set reset(value: boolean) {
    this.pReset = value;
    this.resetSearchTerm();
  }

  public ngOnInit(): void {
    this.form.controls.country.valueChanges.subscribe(x => this.countryChanged.emit(x));
    if (!this.countries?.length) {
      this.locationService.getAllCountries().subscribe((countries: ICountryDtoModel[]) => {
        this.countries = countries;
        const defaultCountry = this.countries?.find(x => x.code === this.form?.controls?.country?.value?.code ? this.form?.controls?.country?.value?.code : 'GB'); //GB to use environment setting instead
        this.form.controls.country.setValue(defaultCountry);
      });
    }
    this.search();
    this.form.controls.country.valueChanges.pipe(
      tap(() => {
        this.locations = [];
        this.searchJustCompleted = false;
      }),
      filter(() => !!this.term && this.term.length >= this.minSearchCharacters),
      tap(() => this.form.enabled),
      tap(() => this.isBusy = true),
      debounceTime(1000),
      mergeMap(() => this.getLocations(this.term)),
      map(locations => locations.map(loc => {
        return {
        address: loc,
        country: this.form.controls.country.value.code
      } as IAddressSearchModel}))
    ).subscribe(locations => {
      this.locations = locations;
      this.showPicker = true;
      this.searchJustCompleted = true;
      this.searchResultEmitter.emit(this.locations);
      this.cd.markForCheck();      
      this.formEmitter.emit(this.form);
      this.isBusy = false;  
    }, (e) => {
      this.reportError(e, this.searchLocationErrorLoading)
      this.isBusy = false;
    });
  }

  public filter(term: string): void {
    this.getLocations(term);
  }

  public onSelectionChange(location: IAddressSearchModel, selectionIndex: number): void {
    
    this.locationService.getAddressResultDetails(location, this.searchControl.value, selectionIndex)
    .subscribe((r: IAddressModel) => {
      this.locationSelected.emit(r);
    },
    (e) => {
      this.reportError(e, this.searchLocationErrorLoading);
      this.errorEmitter.emit(true);
    })

    this.showPicker = false;
    this.justSelected = true;
    
  }

  public search(): void {
    this.form.controls.searchTerm.valueChanges
      .pipe(
        tap((term) => this.locationInputChanged.next(term)),
        tap(() => {
          this.locations = [];
          this.searchJustCompleted = false;
        }),
        tap((term) => this.term = term),
        filter(term => !!term && term.length >= this.minSearchCharacters),
        tap(() => this.isBusy = true),
        debounceTime(1000),
        mergeMap(value => this.getLocations(value)),
        map(locations => locations.map(loc => {
          return {
          address: loc,
          country: this.form.controls.country.value.code
        } as IAddressSearchModel}))
      ).subscribe(locations => {
        this.locations = locations;
        this.showPicker = true;
        this.searchJustCompleted = true;
        this.searchResultEmitter.emit(this.locations);
        this.cd.markForCheck();
        this.formEmitter.emit(this.form); 
        this.isBusy = false;  
      }, (e) => {
        this.reportError(e, this.searchLocationErrorLoading)
        this.isBusy = false;
        this.errorEmitter.emit(true);
      });
  }

  public getLocations(term: string): Observable<string[]> {
    return this.locationService.find({
      address: term,
      country: this.country?.code ? this.country.code : ''
    } as IAddressSearchModel);
  }

  public resetSearchTerm(): void {
    this.form.controls.searchTerm.patchValue('', {emitEvent: false, onlySelf: true});
  }

  public get country(): ICountryDtoModel {
    return this.form?.controls?.country?.value ? this.form.controls.country.value: '';
  }
}
